import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

interface NavbarProps {
  className?: string;
}

interface HamburgerIconProps {
  isOpen: boolean;
  onClick: () => void;
}

function HamburgerIcon({ isOpen, onClick }: HamburgerIconProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative z-50 flex h-10 w-10 flex-col items-center justify-center space-y-1 xl:hidden"
      aria-label={isOpen ? "Close menu" : "Open menu"}
      aria-expanded={isOpen}
    >
      <span
        className={`block h-0.5 w-6 bg-white transition-all duration-300 ease-in-out ${
          isOpen ? "translate-y-1.5 rotate-45" : ""
        }`}
      />
      <span
        className={`block h-0.5 w-6 bg-white transition-all duration-300 ease-in-out ${
          isOpen ? "opacity-0" : ""
        }`}
      />
      <span
        className={`block h-0.5 w-6 bg-white transition-all duration-300 ease-in-out ${
          isOpen ? "-translate-y-1.5 -rotate-45" : ""
        }`}
      />
    </button>
  );
}

export function Navbar({ className = "" }: NavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "unset";
    }

    // Cleanup function to reset overflow when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header className={`tg-nav ${className}`}>
        <div className="tg-nav__wrapper">
          <div className="flex items-center space-x-2">
            <Link to="/" className="tg-primary text-white">
              <img
                src="/images/tennis-logo.webp"
                alt="Tennis Gourmet"
                className="h-8 w-auto lg:h-10"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-6 xl:flex">
            <Link
              to="/restaurant"
              className="tg-nav-item tg-body font-bold"
              activeProps={{
                className: "bg-white text-tennis-800",
              }}
            >
              Ristorante
            </Link>
            <Link
              to="/sport"
              className="tg-nav-item tg-body font-bold"
              activeProps={{
                className: "bg-white text-tennis-800",
              }}
            >
              Sport e relax
            </Link>
            <Link
              to="/about"
              className="tg-nav-item tg-body font-bold"
              activeProps={{
                className: "bg-white text-tennis-800",
              }}
            >
              Chi siamo
            </Link>
            <Link
              to="/contact"
              className="tg-nav-item tg-body font-bold"
              activeProps={{
                className: "bg-white text-tennis-800",
              }}
            >
              Contatti
            </Link>
          </nav>

          {/* Mobile Hamburger Button */}
          <HamburgerIcon isOpen={isMobileMenuOpen} onClick={toggleMobileMenu} />
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`bg-opacity-50 fixed inset-0 z-40 bg-black transition-opacity duration-300 xl:hidden ${
          isMobileMenuOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={closeMobileMenu}
      />

      {/* Mobile Menu */}
      <nav
        className={`bg-tennis-900 fixed top-0 left-0 z-40 h-full w-screen transform transition-transform duration-300 ease-in-out xl:hidden ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex h-full flex-col justify-center gap-6 px-6 py-20 md:px-21">
          <Link
            to="/"
            className="tg-nav-item tg-body block py-4 font-bold"
            activeProps={{
              className: "bg-white text-tennis-800",
            }}
            onClick={closeMobileMenu}
          >
            Home
          </Link>
          <Link
            to="/restaurant"
            className="tg-nav-item tg-body block py-4 font-bold"
            activeProps={{
              className: "bg-white text-tennis-800",
            }}
            onClick={closeMobileMenu}
          >
            Ristorante
          </Link>
          <Link
            to="/sport"
            className="tg-nav-item tg-body block py-4 font-bold"
            activeProps={{
              className: "bg-white text-tennis-800",
            }}
            onClick={closeMobileMenu}
          >
            Sport e relax
          </Link>
          <Link
            to="/about"
            className="tg-nav-item tg-body block py-4 font-bold"
            activeProps={{
              className: "bg-white text-tennis-800",
            }}
            onClick={closeMobileMenu}
          >
            Chi siamo
          </Link>
          <Link
            to="/contact"
            className="tg-nav-item tg-body block py-4 font-bold"
            activeProps={{
              className: "bg-white text-tennis-800",
            }}
            onClick={closeMobileMenu}
          >
            Contatti
          </Link>
        </div>
      </nav>
    </>
  );
}
